import { ComponentProps, ReactNode } from 'react';
import Typography from '@alltrails/core/components/Typography';

type Mark = { value: number; label?: ReactNode };

export type SharedSliderProps<T extends number | [number, number]> = {
  className?: string;
  /** Removes the gap between the label/header row and the subHeader so the subHeader hugs the header. */
  compactSubHeader?: boolean;
  disabled?: boolean;
  formatHeaderValue?: (value: T) => ReactNode;
  headerValueTextVariant?: ComponentProps<typeof Typography>['variant'];
  label?: ReactNode;
  labelTextVariant?: ComponentProps<typeof Typography>['variant'];
  // This component began to get extremely confusing when we supported just passing a min/max value OR passing an array of values that each acted as one "step".
  // To prevent this component from becoming an unmaintainable nightmare, this has been consolidated to just a "marks" array.
  marks: Mark[];
  onChange: (value: T) => void;
  onChangeEnd?: (value: T) => void;
  subHeader?: ReactNode;
  subHeaderTextVariant?: ComponentProps<typeof Typography>['variant'];
  value: T;
  ariaLabelledby?: string;
};

export const thumbSize = 24;

export const findClosestIndex = (marks: number[], value: number) => {
  if (!Array.isArray(marks) || typeof value !== 'number') {
    return -1; // Or throw an error for invalid input
  }

  // Check for a direct match first
  const directMatchIndex = marks.indexOf(value);
  if (directMatchIndex !== -1) {
    return directMatchIndex;
  }

  // If no direct match, find the closest number
  if (marks.length === 0) {
    return -1; // Or handle empty array case as needed
  }

  let closestIndex = 0;
  let minDifference = Math.abs(marks[0] - value);

  for (let i = 1; i < marks.length; i++) {
    const currentNumber = marks[i];
    const currentDifference = Math.abs(currentNumber - value);

    if (currentDifference < minDifference) {
      minDifference = currentDifference;
      closestIndex = i;
    }
  }

  return closestIndex;
};

export const getValueIndex = (marks: Mark[], value: number) => {
  const marksValues = marks.map(mark => mark.value);
  const index = findClosestIndex(marksValues, value);

  if (index === -1) {
    throw new Error('The Slider value must be one of the provided mark values.');
  }
  return index;
};
